/**
 * src/components/productPrimitives.js
 *
 * Shared presentation primitives for the VISINVOKE product surfaces.
 *
 * One release-state vocabulary and one price presentation, used by:
 *   - the paid catalogue cards (PaidUtilitiesPage)
 *   - the free utility cards (FreeUtilityCard in UtilitiesPage)
 *   - the Utilities dashboard "IN THIS CATALOGUE" rows
 *   - the product detail pages (ProductDetailPage buy panel)
 *
 * so an unreleased product can never read as available, an available product
 * always reads AVAILABLE NOW, and a price always shows its amount large with
 * the billing unit separated.
 *
 * IMPORTANT: nothing here invents availability, pricing or status. Every value
 * is derived from the REAL status / price strings already stored on the product.
 * resolveReleaseState only classifies the wording that is already there.
 */
import React from 'react';
import { hexA } from '../RegistryComponents';
import { glassSurface } from '../config/glassTheme';

// Tones shared across every surface.
export const RELEASE_TONE = {
  available: '#34d399',   // live, usable now
  coming: '#fbbf24',      // announced, not yet available
  development: '#60a5fa',  // actively being built
  neutral: '#94a3b8',
};

/**
 * Classify a real status string into a normalised release state.
 * Returns { key, label, tone, live }. `label` is upper-cased for the badge.
 * Unknown wording is preserved verbatim (upper-cased) rather than dropped, so
 * nothing a product actually says is ever lost or mislabelled.
 */
export function resolveReleaseState(raw) {
  const s = String(raw == null ? '' : raw).trim();
  const low = s.toLowerCase();

  if (!s) return { key: 'unknown', label: '', tone: RELEASE_TONE.neutral, live: false };

  if (low === 'available' || low === 'available now' || low === 'active' || low === 'live') {
    return { key: 'available', label: 'AVAILABLE NOW', tone: RELEASE_TONE.available, live: true };
  }
  if (low.indexOf('in development') === 0 || low === 'in dev' || low === 'development') {
    return { key: 'in-development', label: 'IN DEVELOPMENT', tone: RELEASE_TONE.development, live: false };
  }
  if (low.indexOf('due') === 0) {
    // Preserve the exact due wording the product carries (Due 2027, Due Early 2027, ...).
    return { key: 'due', label: s.toUpperCase(), tone: RELEASE_TONE.coming, live: false };
  }
  if (low.indexOf('coming') === 0) {
    return { key: 'coming-soon', label: 'COMING SOON', tone: RELEASE_TONE.coming, live: false };
  }
  if (low.indexOf('subscription') !== -1) {
    return { key: 'subscription', label: s.toUpperCase(), tone: RELEASE_TONE.coming, live: false };
  }
  return { key: 'other', label: s.toUpperCase(), tone: RELEASE_TONE.neutral, live: !!/available|active|live|ready/.test(low) };
}

/**
 * A single, consistent status pill. Accepts either a raw status string via
 * `status`, or a pre-resolved state via `state` (which only needs { label, tone }).
 *
 * variant:
 *   'default' — tinted pill for dark navy surfaces (paid cards, detail page).
 *   'onColor' — dark translucent pill for saturated coloured surfaces (free cards).
 */
export function AvailabilityBadge({ status, state, tone, variant = 'default', size = 'md', style, dotOnly = false }) {
  const rs = state || resolveReleaseState(status);
  if (!rs || !rs.label) return null;
  const t = tone || rs.tone || RELEASE_TONE.neutral;

  const pad = size === 'sm' ? '4px 10px' : '6px 13px';
  const fs = size === 'sm' ? 11 : 12;

  const base = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
    borderRadius: 999,
    border: '1px solid',
    padding: pad,
    fontSize: fs,
    fontWeight: 800,
    letterSpacing: '0.04em',
    whiteSpace: 'nowrap',
    lineHeight: 1.2,
  };

  const skin = variant === 'onColor'
    ? { background: 'rgba(3,9,18,0.42)', borderColor: 'rgba(255,255,255,0.30)', color: '#ffffff' }
    : glassSurface(t, t, t);

  return (
    <span style={{ ...base, ...skin, ...style }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: t, flexShrink: 0 }} />
      {!dotOnly ? rs.label : null}
    </span>
  );
}

/**
 * FREE pill, paired with an AVAILABLE NOW / COMING SOON badge on the free cards.
 */
export function FreeBadge({ variant = 'onColor', size = 'md', style }) {
  const t = RELEASE_TONE.available;
  const pad = size === 'sm' ? '4px 10px' : '6px 13px';
  const fs = size === 'sm' ? 11 : 12;
  const base = {
    display: 'inline-flex', alignItems: 'center', gap: 7, borderRadius: 999,
    border: '1px solid', padding: pad, fontSize: fs, fontWeight: 900,
    letterSpacing: '0.08em', whiteSpace: 'nowrap', lineHeight: 1.2,
  };
  const skin = variant === 'onColor'
    ? { background: 'rgba(3,9,18,0.42)', borderColor: 'rgba(255,255,255,0.30)', color: '#ffffff' }
    : glassSurface(t, t, t);
  return <span style={{ ...base, ...skin, ...style }}>FREE</span>;
}

/**
 * Price with the amount shown large and the billing unit separated.
 * `price` is the amount string ("$29.99 USD"), `unit` the billing cadence
 * ("Per Month / Company"), `note` an optional qualifier line.
 */
export function ProductPriceBlock({ price, unit, note, size = 'md', align = 'left', style, priceStyle, unitStyle }) {
  const amountSize = size === 'lg' ? 40 : size === 'sm' ? 26 : 34;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: align === 'center' ? 'center' : 'flex-start', ...style }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 9, flexWrap: 'wrap' }}>
        <span style={{ fontSize: amountSize, fontWeight: 900, color: '#ffffff', letterSpacing: '-0.8px', lineHeight: 1.02, ...priceStyle }}>{price}</span>
        {unit ? <span style={{ fontSize: 14, fontWeight: 700, color: 'rgba(255,255,255,0.62)', ...unitStyle }}>{unit}</span> : null}
      </div>
      {note ? <div style={{ fontSize: 12.5, fontWeight: 600, color: 'rgba(255,255,255,0.58)', lineHeight: 1.5 }}>{note}</div> : null}
    </div>
  );
}
